"use client";

import { useCallback, useState } from "react";

import type { DiscoverFilterFields } from "@/components/app/discover-sort-constants";
import { TimePeriod } from "@/components/app/discover-sort-constants";
import type { PartialAccount } from "@/utils/types";

export type FiltersSortContextType<T extends string = string> = {
  filters: FilterRecord[];
  isFilterActive: (name: T) => boolean;
  updateFilter: (filter: FilterRecord, clearPrevFilters?: boolean) => void;
  findFilterByName: (name: T) => FilterRecord | undefined;
  currentSort: SortVariant<T> | null;
  isSortActive: (name: T) => boolean;
  removeFilter: (name: T) => void;
  setFilters: (filters: FilterRecord[], syncLastSaved?: boolean) => void;
  setCurrentSort: (
    sort: SortVariant<T> | null,
    syncLastSaved?: boolean,
  ) => void;
};

export enum FilterType {
  string = "string",
  multiSelect = "multiSelect",
  select = "select",
  singleNumber = "singleNumber",
  boolean = "boolean",
  matchString = "matchString",
  numeric = "numeric",
  numericWithTime = "numericWithTimePeriod",
  date = "date",
  multiUserSelect = "multiUserSelect",
}

export enum SortType {
  basic = "basic",
  numericWithTime = "numericWithTimePeriod",
}

export type BasicSort<T> = {
  type: SortType.basic;
  name: T;
  direction: "asc" | "desc";
};

export type NumericSort<T> = {
  type: SortType.numericWithTime;
  name: T;
  direction: "asc" | "desc";
  timePeriod: TimePeriod;
};

export type SortVariant<T> = BasicSort<T> | NumericSort<T>;

export const TimePeriodLabels = {
  [TimePeriod.AllTime]: "All Time",
  [TimePeriod.Past24H]: "Last 24 Hours",
  [TimePeriod.Past7D]: "Last 7 Days",
  [TimePeriod.Past30D]: "Last 30 Days",
};

export const TimePeriodMap = {
  [TimePeriod.AllTime]: 0,
  [TimePeriod.Past24H]: 1,
  [TimePeriod.Past7D]: 7,
  [TimePeriod.Past30D]: 30,
  0: TimePeriod.AllTime,
  1: TimePeriod.Past24H,
  7: TimePeriod.Past7D,
  30: TimePeriod.Past30D,
} as const;

export type FilterMatchStringValue = {
  any?: string;
  all?: string;
  none?: string;
};

export type FilterNumericValue = {
  min?: number;
  max?: number;
};

export type FilterNumericValueWithTimePeriod = {
  timePeriod: TimePeriod;
  min?: number;
  max?: number;
};

export type FilterDateValue = {
  after?: Date;
  before?: Date;
};

export type FilterRecord =
  | {
      type: FilterType.string;
      name: string;
      value: string;
    }
  | {
      type: FilterType.matchString;
      name: string;
      value: FilterMatchStringValue;
    }
  | {
      type: FilterType.numeric;
      name: string;
      value: FilterNumericValue;
    }
  | {
      type: FilterType.numericWithTime;
      name: string;
      value: FilterNumericValueWithTimePeriod;
    }
  | {
      type: FilterType.date;
      name: string;
      value: FilterDateValue;
    }
  | {
      type: FilterType.boolean;
      name: string;
      value: boolean;
    }
  | {
      type: FilterType.select;
      name: string;
      value: string;
    }
  | {
      type: FilterType.multiSelect;
      name: string;
      value: string[];
    }
  | {
      type: FilterType.singleNumber;
      name: string;
      value: number;
    }
  | {
      type: FilterType.multiUserSelect;
      name: string;
      value: PartialAccount[];
    };

type FilterSortProviderProps<T> = {
  initialFilters?: FilterRecord[];
  initialSort?: SortVariant<T> | null;
  disabledFilters?: string[];
};

export const useFiltersAndSort = <T extends string = DiscoverFilterFields>({
  initialFilters = [],
  initialSort = null,
  disabledFilters = [],
}: FilterSortProviderProps<T>) => {
  const [lastSavedFilters, setLastSavedFilters] = useState<FilterRecord[]>([]);
  const [lastSavedSorts, setLastSavedSorts] = useState<SortVariant<T> | null>(
    null,
  );
  const [filters, setFiltersState] = useState<FilterRecord[]>(initialFilters);
  const [currentSort, setCurrentSortState] = useState<SortVariant<T> | null>(
    initialSort,
  );

  const setFilters = useCallback(
    (nextFilters: FilterRecord[], syncLastSaved?: boolean) => {
      setFiltersState(nextFilters);

      if (syncLastSaved) {
        setLastSavedFilters(nextFilters);
      }
    },
    [],
  );

  const setCurrentSort = useCallback(
    (nextSort: SortVariant<T> | null, syncLastSaved?: boolean) => {
      setCurrentSortState(nextSort);

      if (syncLastSaved) {
        setLastSavedSorts(nextSort);
      }
    },
    [],
  );

  const findFilterByName = useCallback(
    (name: string, fromSaved = false) =>
      (fromSaved ? lastSavedFilters : filters).find((f) => f.name === name),
    [filters, lastSavedFilters],
  );

  const updateFilter = useCallback(
    (
      filter: FilterRecord,
      clearPrevFilters?: boolean,
      syncWithSavedFilters?: boolean,
    ) => {
      if (clearPrevFilters) {
        setFiltersState([filter]);
        return;
      }

      const findAndUpdateFilter = (prevFilters: FilterRecord[]) => {
        const filterToUpdate = prevFilters.find((f) => f.name === filter.name);

        if (!filterToUpdate) {
          return [...prevFilters, filter];
        }

        return prevFilters.map((f) => {
          if (f.name === filter.name) {
            return filter;
          }

          return f;
        });
      };

      setFiltersState(findAndUpdateFilter);
      if (syncWithSavedFilters) {
        setLastSavedFilters(findAndUpdateFilter);
      }
    },
    [],
  );

  const isFilterActive = useCallback(
    <T extends string>(name: T) => {
      const filter = filters.find((f) => f.name === name);

      if (filter?.type === FilterType.string) {
        return filter.value !== "";
      }

      if (filter?.type === FilterType.matchString) {
        return (
          filter.value?.any !== "" ||
          filter.value?.all !== "" ||
          filter.value?.none !== ""
        );
      }

      if (filter?.type === FilterType.numeric) {
        return filter.value?.min !== 0 || filter.value?.max !== 0;
      }

      if (filter?.type === FilterType.numericWithTime) {
        return (
          filter.value?.min !== 0 ||
          filter.value?.max !== 0 ||
          filter.value?.timePeriod !== TimePeriod.AllTime
        );
      }

      if (filter?.type === FilterType.select) {
        return !!filter.value;
      }

      if (filter?.type === FilterType.singleNumber) {
        return !!filter.value || filter.value === 0;
      }

      if (filter?.type === FilterType.date) {
        return !!filter.value?.after || !!filter.value?.before;
      }

      if (filter?.type === FilterType.boolean) {
        return filter.value !== undefined;
      }

      if (filter?.type === FilterType.multiSelect) {
        return filter.value.length > 0;
      }

      if (filter?.type === FilterType.multiUserSelect) {
        return filter.value.length > 0;
      }

      return false;
    },
    [filters],
  );

  const isSortActive = useCallback(
    (name: string) => {
      return currentSort?.name === name;
    },
    [currentSort],
  );

  const removeFilter = useCallback(
    <T extends string>(name: T, syncLastSavedFilters?: boolean) => {
      const r = (prevFilters: FilterRecord[]) =>
        prevFilters.filter((f) => f.name !== name);

      setFiltersState(r);
      if (syncLastSavedFilters) {
        setLastSavedFilters(r);
      }
    },
    [],
  );

  const syncLastSavedFilters = useCallback(() => {
    setLastSavedFilters(filters);
  }, [filters]);

  const setFiltersToLastSaved = useCallback(() => {
    setFiltersState(lastSavedFilters);
  }, [lastSavedFilters]);

  const syncLastSavedSorts = useCallback(() => {
    setLastSavedSorts(currentSort);
  }, [currentSort]);

  const setSortsToLastSaved = useCallback(() => {
    setCurrentSortState(lastSavedSorts);
  }, [lastSavedSorts]);

  const isFilterDisabled = (filter: string) => disabledFilters.includes(filter);

  return {
    setFilters,
    findFilterByName,
    removeFilter,
    filters,
    isSortActive,
    isFilterActive,
    updateFilter,
    currentSort,
    setFiltersToLastSaved,
    syncLastSavedFilters,
    syncLastSavedSorts,
    setSortsToLastSaved,
    isFilterDisabled,
    setCurrentSort,
  };
};
